import { useNavigate } from "react-router-dom";

import { Button, Form, Input, message } from "antd";
import axios from "axios";
import React, { FC, useState } from "react";

import OtpModal from "@/components/common/OTPModal";
import SuccessModal from "@/components/common/SuccessModal";
import { IMAGES, ROUTES_URL } from "@/constant";
import styles from "@/pages/ForgotPassword/ForgotPassword.module.css";
import { forgotPassword, resendLoginOtp, submitOtpNew } from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";

const ForgotPassword: FC = () => {
  const { email, isTCAccepted, hashKey, updateState } = useAuthStore();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [otpModal, setOtpModal] = useState(false);

  const handleForgotPassword = async (values: { email: string }): Promise<void> => {
    try {
      const { data } = await forgotPassword({ user_id: values.email });
      updateState({ email: values.email, isTCAccepted: !data.isFirstLogin });
      setOtpModal(true);
    } catch (error: unknown) {
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message || "Login failed");
      }
    }
  };

  const handleSubmitOTP = async (otp: string): Promise<void> => {
    if (!email) return;
    try {
      const { data } = await submitOtpNew({ otp, email: email });

      updateState({ hashKey: data?.reset_password_token });

      if (data.is_verified) {
        message.success("OTP verified! Please reset your password");
        navigate(ROUTES_URL.RESET_PASSWORD);
      }
    } catch (error: unknown) {
      console.error(error);
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message);
      }
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    if (!email) return;
    try {
      await resendLoginOtp({
        user_id: email,
      });
    } catch (error: unknown) {
      console.error(error);
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message);
      }
    }
  };

  const handleCloseModal = (): void => {
    setOtpModal(false);
  };

  return (
    <div className={styles.gradientBg}>
      {confirm ? (
        <SuccessModal
          title="A temporary one-time use password has been sent to your email."
          open={confirm}
          isBackToLogin={true}
        />
      ) : (
        <div className={styles.formContainer}>
          <h2 className={styles.loginHeading}>Forgot Password?</h2>
          <p className={styles.loginSubTitle}>
            Please enter your email we’ll send you a temporary password.
          </p>

          <Form form={form} name="forgotPassword" layout="vertical" onFinish={handleForgotPassword}>
            <Form.Item
              name="email"
              className={styles.userId}
              label="Email"
              required={false}
              rules={[{ required: true, type: "email", message: "Please enter a valid email" }]}
            >
              <Input
                placeholder="Enter your email"
                prefix={<img src={IMAGES.USER_ICON} className={styles.userIcon} />}
              />
            </Form.Item>

            <Form.Item className={styles.login}>
              <Button block htmlType="submit" loading={loading} className={styles.submitButton}>
                Send Email
              </Button>
            </Form.Item>
          </Form>

          <div className={styles.footer} onClick={() => navigate(ROUTES_URL.LOGIN)}>
            <img src={IMAGES.LOGIN_RIGHT_ARROW} alt="back to login" /> Back to Login
          </div>
        </div>
      )}
      {otpModal ? (
        <OtpModal
          open={otpModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmitOTP}
          onSubmitResend={handleResendOTP}
        />
      ) : null}
    </div>
  );
};

export default ForgotPassword;
